import {
  DocumentationGroupV1,
  DocumentationItemConfigurationV1,
  DocumentationPageV1,
  DocumentationPageV2,
  groupBy,
} from "@supernova-studio/model";
import { DTODocumentationGroupStructureV1, DTODocumentationGroupV1 } from "../../dto/elements/documentation/group-v1";
import {
  documentationItemConfigurationToDTOV1,
  getDtoDefaultItemConfigurationV1,
} from "./documentation-item-configuration-v1-to-dto";
import { applyPrivacyConfigurationToNestedItems } from "./documentation-page-to-dto-utils";

// The fact that DTO conversion is located here instead of main backend code is due to the fact
// that we store page and group data in YJS documents in the same way as they are stored in the database.
// Therefore, we need to expose this conversion to the client so that it can consume data from YJS documents.
//
// Please do not put more DTO conversion here unless you know what you're doing.

export function elementGroupsToDocumentationGroupStructureDTOV1(
  groups: DocumentationGroupV1[],
  pages: (DocumentationPageV1 | DocumentationPageV2)[]
): DTODocumentationGroupStructureV1[] {
  const childrenIdsMap = calculateChildrenIdsMapV1(pages, groups);

  return groups.map(group => elementGroupToDocumentationGroupStructureDTOV1(group, childrenIdsMap));
}

export function elementGroupsToDocumentationGroupDTOV1(
  groups: DocumentationGroupV1[],
  pages: (DocumentationPageV1 | DocumentationPageV2)[]
): DTODocumentationGroupV1[] {
  const childrenIdsMap = calculateChildrenIdsMapV1(pages, groups);

  return groups.map(group => {
    return {
      ...elementGroupToDocumentationGroupStructureDTOV1(group, childrenIdsMap),
      configuration: documentationItemConfigurationToDTOV1(group.data?.configuration),
    };
  });
}

export function elementGroupsToDocumentationGroupFixedConfigurationDTOV1(
  groups: DocumentationGroupV1[],
  pages: (DocumentationPageV1 | DocumentationPageV2)[]
): DTODocumentationGroupV1[] {
  const childrenIdsMap = calculateChildrenIdsMapV1(pages, groups);

  const { groups: fixedGroups } = applyPrivacyConfigurationToNestedItems<
    DocumentationPageV1 | DocumentationPageV2,
    DocumentationGroupV1,
    DocumentationItemConfigurationV1
  >(pages, groups, getDtoDefaultItemConfigurationV1);

  return fixedGroups.map(group => {
    const configuration = documentationItemConfigurationToDTOV1(group.data?.configuration);

    return {
      ...elementGroupToDocumentationGroupStructureDTOV1(group, childrenIdsMap),
      // backward compatibility with custom doc exporters, anyway these groups will not be shown, so we can adjust title
      title: configuration.isHidden && !group.meta.name.startsWith("_") ? "_" + group.meta.name : group.meta.name,
      configuration,
    };
  });
}

function elementGroupToDocumentationGroupStructureDTOV1(
  group: DocumentationGroupV1,
  childrenIdsMap: Map<string, string[]>
): DTODocumentationGroupStructureV1 {
  const childrenIds = childrenIdsMap.get(group.persistentId) ?? [];

  if (!group.shortPersistentId) {
    throw new Error(`Short persistent id is required for docs groups, group id: ${group.id}`);
  }

  return {
    id: group.id,
    designSystemVersionId: group.designSystemVersionId,
    persistentId: group.persistentId,
    slug: group.slug,
    userSlug: group.userSlug,
    createdAt: group.createdAt,
    updatedAt: group.updatedAt,
    title: group.meta.name,
    childrenIds: childrenIds,
    isRoot: !group.parentPersistentId,
    groupBehavior: group.data?.behavior ?? "Group",
    shortPersistentId: group.shortPersistentId,
    type: "Group",
  };
}

function calculateChildrenIdsMapV1(
  elements: { persistentId: string; parentPersistentId: string; sortOrder: number }[],
  groups: DocumentationGroupV1[]
): Map<string, string[]> {
  const byParentId = groupBy([...elements, ...groups], e => e.parentPersistentId);

  const childrenIdsMap = new Map<string, string[]>();

  for (const [parentPersistentId, children] of byParentId) {
    if (!parentPersistentId) continue;
    children.sort((lhs, rhs) => lhs.sortOrder - rhs.sortOrder);

    childrenIdsMap.set(
      parentPersistentId,
      children.map(c => c.persistentId)
    );
  }

  return childrenIdsMap;
}
